import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCustomerList } from "gen/endpoints/customer/customer";
import { useInternalIntegrationTestingCustomerComparisonCreate } from "gen/endpoints/integration-testing-customer-comparison/integration-testing-customer-comparison";
import { getInternalIntegrationTestingQueueTableQueryKey } from "gen/endpoints/integration-testing-queue/integration-testing-queue";
import { TestingCustomerComparisonCreateDataRequest } from "gen/model/testingCustomerComparisonCreateDataRequest";
import { useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";

export interface InternalIntegrationTestingCustomerComparisonModalProps {
  onCloseComplete(): void;
}

export const InternalIntegrationTestingCustomerComparisonModal: React.FC<
  InternalIntegrationTestingCustomerComparisonModalProps
> = ({ onCloseComplete }) => {
  const { t } = useTranslation(["internal", "auth", "common"]);
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const form = useForm<TestingCustomerComparisonCreateDataRequest>();

  const customerOneName = useWatch({
    control: form.control,
    name: "customer_1_name",
  }) as string | null;

  const { data: customerListData } = useCustomerList();

  const { mutateAsync: createComparison } =
    useInternalIntegrationTestingCustomerComparisonCreate();

  const queryClient = useQueryClient();

  const onSubmit = async (data: TestingCustomerComparisonCreateDataRequest) => {
    await createComparison({ data });
    await queryClient.invalidateQueries({
      queryKey: getInternalIntegrationTestingQueueTableQueryKey(),
    });
  };

  const customerTwoSelectOptions = useMemo(
    () =>
      customerListData?.map((customer) => {
        if (customer.name === customerOneName) {
          return null;
        }

        return (
          <SelectOption
            key={`customer_2_name_${customer.name}`}
            value={customer.name}
          >
            {customer.name}
          </SelectOption>
        );
      }),
    [customerOneName, customerListData],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <Form
          form={form}
          onSubmit={onSubmit}
          onSuccess={onClose}
          successMessage={t("internal:modal.customerComparison.success")}
        >
          <ModalHeader>
            {t("internal:modal.customerComparison.title")}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <Text marginBottom={4} fontSize={14} fontWeight={400}>
              {t("internal:modal.customerComparison.description")}
            </Text>
            <FormSelect
              title={t(
                "internal:integrationTesting.customerComparison.customerOne",
              )}
              name="customer_1_name"
            >
              {customerListData?.map((customer) => (
                <SelectOption
                  key={`customer_1_name_${customer.name}`}
                  value={customer.name}
                >
                  {customer.name}
                </SelectOption>
              ))}
            </FormSelect>
            {!!customerOneName && (
              <FormSelect
                title={t(
                  "internal:integrationTesting.customerComparison.customerTwo",
                )}
                name="customer_2_name"
              >
                {customerTwoSelectOptions}
              </FormSelect>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={onClose} autoFocus={true} />}
              confirm={<FormSubmitButton>{t("common:save")}</FormSubmitButton>}
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
